import { Fragment, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { RefreshRounded } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

import LoadingWrapper from '../../components/LoadingWrapper';
import EditProduct from '../../components/product/EditProduct';

import { useCompany, useProduct } from '../../stores';

import { getUnitType } from '../../utils/product';

const ROWS_PER_PAGE = 15;

const ProductTypes: { [key: string]: string } = {
  PRODUCT: 'Vara',
  SERVICE: 'Tjänst',
};

const ProductsView = observer(() => {
  const companyStore = useCompany();
  const productStore = useProduct();

  const [page, setPage] = useState<number>(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [editProductOpen, setEditProductOpen] = useState<boolean>(false);
  const [selectedProductId, setSelectedProductId] = useState<string>('');

  const { t } = useTranslation();

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);

    const lastItemIndex = (newPage + 1) * ROWS_PER_PAGE - 1;

    if (lastItemIndex + 1 >= productStore.list.length && !productStore.endReached) {
      productStore.fetchMoreProducts();
    }
  };

  const handleOpenDeleteDialog = (productId: string) => {
    if (!productId) return;

    setSelectedProductId(productId);
    setDeleteDialogOpen(true);
  };

  const handleOpenEdit = (productId: string) => {
    if (!productId) return;

    setSelectedProductId(productId);
    setEditProductOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setSelectedProductId('');
    setDeleteDialogOpen(false);
  };

  useEffect(() => {
    productStore.fetchInitialProducts();
  }, [companyStore.companyKey]);

  return (
    <Box>
      {productStore.errorMessage && (
        <Box sx={{ marginTop: 2, marginBottom: 2 }}>
          <Alert severity="error">{productStore.errorMessage}</Alert>
        </Box>
      )}
      <Typography variant="h1" gutterBottom>
        {t('productScreen.products')}
      </Typography>
      <Paper sx={{ marginTop: 2, marginBottom: 2, padding: '1rem' }}>
        <LoadingWrapper loading={productStore.loading && productStore.list.length === 0}>
          <TableContainer sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('productScreen.name')}</TableCell>
                  <TableCell>{t('productScreen.priceType')}</TableCell>
                  <TableCell align="right">{t('productScreen.price')}</TableCell>
                  <TableCell>{t('productScreen.productType')}</TableCell>
                  <TableCell align="right">{t('productScreen.vat')}</TableCell>
                  <TableCell>{t('productScreen.image')}</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {productStore.list
                  .slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE)
                  .map((product) => (
                    <Fragment key={product.id}>
                      <TableRow sx={{ '& > td': { borderBottom: 'none' }, p: 0 }}>
                        <TableCell>{product.title}</TableCell>
                        <TableCell>{product.priceType}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          {product.price
                            ? `${product.price.format()}/${
                                getUnitType(product.unitType, companyStore.data?.customUnits).pre
                              }`
                            : null}
                        </TableCell>
                        <TableCell>{ProductTypes[product.type] || product.type}</TableCell>
                        <TableCell align="right">{product.tax} %</TableCell>

                        <TableCell sx={{ width: '50px' }}>
                          {product.image ? (
                            <img src={product.image} width="50px" alt="productimage" />
                          ) : (
                            <>{t('productScreen.missingImage')}</>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {product.id && (
                            <Box>
                              <IconButton onClick={() => handleOpenEdit(product.id)}>
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                color="error"
                                onClick={() => handleOpenDeleteDialog(product.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  ))}
              </TableBody>
            </Table>
            <Box
              sx={{ flexDirection: 'row', display: 'flex', justifyContent: 'space-between', p: 1 }}
            >
              <Box sx={{ flexDirection: 'row', display: 'flex' }}>
                <TablePagination
                  count={productStore.endReached ? productStore.list.length : -1}
                  rowsPerPageOptions={[ROWS_PER_PAGE]}
                  component={Box}
                  rowsPerPage={ROWS_PER_PAGE}
                  page={page}
                  onPageChange={handleChangePage}
                />
                {productStore.loading && <CircularProgress color="secondary" />}
              </Box>
              <IconButton
                aria-label="refresh"
                size="small"
                onClick={() => {
                  productStore.refreshProducts();
                  setPage(0);
                }}
                sx={{ m: 1 }}
              >
                <RefreshRounded />
              </IconButton>
            </Box>
          </TableContainer>
        </LoadingWrapper>
      </Paper>
      <EditProduct
        productId={selectedProductId}
        open={editProductOpen}
        onClose={() => setEditProductOpen(false)}
      />
      {selectedProductId && (
        <Dialog
          open={deleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          aria-describedby="alert-dialog-description"
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {t('productScreen.productWillBeRemoved')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                productStore.removeProduct(selectedProductId).finally(() => {
                  setDeleteDialogOpen(false);
                })
              }
              color="error"
              variant="contained"
            >
              {t('productScreen.yes')}
            </Button>
            <Button onClick={handleCloseDeleteDialog} variant="contained" autoFocus>
              {t('productScreen.no')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
});

export default ProductsView;
